/**
 * Environment Configuration Service
 * Centralized configuration management with proper fallbacks
 */

class EnvironmentConfig {
  constructor() {
    this.config = {
      // Application Environment
      isDevelopment: import.meta.env.DEV ?? false,
      isProduction: import.meta.env.PROD ?? true,
      
      // Supabase Configuration
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      
      // API Keys
      opencageApiKey: import.meta.env.VITE_OPENCAGE_API_KEY,
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      openrouteApiKey: import.meta.env.VITE_OPENROUTE_API_KEY,
      
      // Security
      reviewCode: import.meta.env.VITE_REVIEW_CODE || "202224",
      adminUsername: import.meta.env.VITE_ADMIN_USERNAME || "admin",
      adminEmail: import.meta.env.VITE_ADMIN_EMAIL || "akiblackcar@gmail.com",
      
      // Performance
      enablePerformanceMonitoring: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING !== 'false',
      enableErrorReporting: import.meta.env.VITE_ENABLE_ERROR_REPORTING !== 'false',
      
      // Cache Configuration
      cacheTTLSuggestions: parseInt(import.meta.env.VITE_CACHE_TTL_SUGGESTIONS) || 300000,
      cacheTTLGeocoding: parseInt(import.meta.env.VITE_CACHE_TTL_GEOCODING) || 600000,
      cacheTTLRouting: parseInt(import.meta.env.VITE_CACHE_TTL_ROUTING) || 300000,
      maxLearnedLocations: parseInt(import.meta.env.VITE_MAX_LEARNED_LOCATIONS) || 1000,
      
      // Debug Mode
      debugMode: import.meta.env.VITE_DEBUG_MODE === 'true'
    };
    
    this.validateRequiredConfig();
  }

  /**
   * Validate required configuration values
   * @throws {Error} If required configuration is missing
   */
  validateRequiredConfig() {
    const required = ['supabaseUrl', 'supabaseAnonKey'];
    const missing = required.filter(key => !this.config[key]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
    }
  }

  /**
   * Get configuration value
   * @param {string} key - Configuration key
   * @param {*} defaultValue - Default value if key not found
   * @returns {*} Configuration value
   */
  get(key, defaultValue = undefined) {
    return this.config[key] ?? defaultValue;
  }

  /**
   * Check if running in development mode
   * @returns {boolean} True if development mode
   */
  isDev() {
    return this.config.isDevelopment;
  }

  /**
   * Check if running in production mode
   * @returns {boolean} True if production mode
   */
  isProd() {
    return this.config.isProduction;
  }

  /**
   * Get Supabase configuration
   * @returns {Object} Supabase configuration
   */
  getSupabaseConfig() {
    return {
      url: this.config.supabaseUrl,
      anonKey: this.config.supabaseAnonKey
    };
  }

  /**
   * Get API keys configuration
   * @returns {Object} API keys configuration
   */
  getApiKeys() {
    return {
      opencage: this.config.opencageApiKey,
      googleMaps: this.config.googleMapsApiKey,
      openroute: this.config.openrouteApiKey
    };
  }

  /**
   * Get security configuration
   * @returns {Object} Security configuration
   */
  getSecurityConfig() {
    return {
      reviewCode: this.config.reviewCode,
      adminUsername: this.config.adminUsername,
      adminEmail: this.config.adminEmail
    };
  }

  /**
   * Get cache configuration
   * @returns {Object} Cache configuration
   */
  getCacheConfig() {
    return {
      suggestionsTTL: this.config.cacheTTLSuggestions,
      geocodingTTL: this.config.cacheTTLGeocoding,
      routingTTL: this.config.cacheTTLRouting,
      maxLearnedLocations: this.config.maxLearnedLocations
    };
  }

  /**
   * Check if debug mode is enabled
   * @returns {boolean} True if debug mode enabled
   */
  isDebugMode() {
    return this.config.debugMode;
  }
}

// Export singleton instance
export default new EnvironmentConfig();
